import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { alpha } from "@mui/material/styles";
import {
  Typography, Button, Paper, Box, Snackbar, Alert, CircularProgress, Chip
} from "@mui/material";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
import HourglassEmptyOutlinedIcon from "@mui/icons-material/HourglassEmptyOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import {
  getEstadoConnect, iniciarOnboardingConnect, abrirDashboardConnect
} from "../../../actions/payments";

const SectionHeader = ({ title, subtitle }) => (
  <Box mb={3}>
    <Typography variant="h6" color="primary" fontWeight="bold">
      {title}
    </Typography>
    <Typography variant="body2" color="text.secondary">
      {subtitle}
    </Typography>
  </Box>
);

// Beneficios de conectar la cuenta (se muestran mientras no esté lista)
const Beneficio = ({ children }) => (
  <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
    <CheckCircleOutlineOutlinedIcon sx={{ fontSize: 18, color: "success.main", mt: "2px", flexShrink: 0 }} />
    <Typography variant="body2" color="text.secondary">{children}</Typography>
  </Box>
);

const Payments = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [estado, setEstado] = useState(null); // respuesta de getEstadoConnect
  const [cargando, setCargando] = useState(true);
  const [ocupado, setOcupado] = useState(false);
  const [toast, setToast] = useState(null); // { tipo, msg }

  const cargarEstado = useCallback(async () => {
    const data = await getEstadoConnect();
    setEstado(data);
    setCargando(false);
  }, []);

  useEffect(() => {
    cargarEstado();
    // Al volver del onboarding (?connect=return|refresh) se limpia el parámetro de la URL
    if (searchParams.get("connect")) {
      const params = new URLSearchParams(searchParams);
      params.delete("connect");
      setSearchParams(params, { replace: true });
    }
  }, []);

  const handleConectar = async () => {
    setOcupado(true);
    const res = await iniciarOnboardingConnect();
    if (res?.url) {
      window.location.href = res.url; // redirige al onboarding alojado por Stripe
    } else {
      setOcupado(false);
      setToast({ tipo: "error", msg: res?.msg || "No se pudo iniciar la conexión con Stripe. Intenta de nuevo." });
    }
  };

  const handleDashboard = async () => {
    setOcupado(true);
    const res = await abrirDashboardConnect();
    if (res?.url) {
      window.location.href = res.url;
    } else {
      setOcupado(false);
      setToast({ tipo: "error", msg: res?.msg || "No se pudo abrir tu panel de Stripe. Intenta de nuevo." });
    }
  };

  // Deriva el estado visual de la cuenta a partir de la respuesta del backend
  const disponible = estado?.disponible !== false;
  const conectado = !!estado?.conectado;
  const listo = !!estado?.payoutsEnabled;          // ya puede recibir dispersiones
  const enviado = !!estado?.detailsSubmitted;       // completó el formulario, en verificación

  return (
    <Box>
      <SectionHeader
        title="Recibe tus pagos"
        subtitle="Conecta tu cuenta con Stripe para que tus pagos se depositen automáticamente en tu banco"
      />

      {cargando ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={28} />
        </Box>
      ) : !disponible ? (
        <Alert severity="info" sx={{ borderRadius: 2 }}>
          Los pagos en línea aún no están configurados en la plataforma. Vuelve más tarde o
          contacta al administrador.
        </Alert>
      ) : listo ? (
        /* Cuenta lista: recibe pagos automáticamente */
        <Paper
          variant="outlined"
          sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, borderColor: "success.claro", borderWidth: 2, bgcolor: (t) => alpha(t.palette.success.main, 0.07) }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Box sx={{
              width: 48, height: 48, borderRadius: "12px", bgcolor: "success.claro", flexShrink: 0,
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              <CheckCircleOutlineOutlinedIcon sx={{ color: "primary.main", fontSize: 28 }} />
            </Box>
            <Box>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  Tu cuenta está lista
                </Typography>
                <Chip label="Activa" size="small"
                  sx={{ bgcolor: "success.claro", color: "primary.main", fontWeight: 700 }} />
              </Box>
              <Typography variant="body2" color="text.secondary">
                Recibes tus pagos automáticamente cuando completas un servicio.
              </Typography>
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Cada vez que un servicio se marca como completado, transferimos tu parte (90%) a tu
            cuenta de Stripe y de ahí se deposita a tu banco según el calendario de Stripe.
          </Typography>
          <Button
            variant="outlined"
            onClick={handleDashboard}
            disabled={ocupado}
            endIcon={<OpenInNewOutlinedIcon />}
            sx={{ textTransform: "none", fontWeight: "bold", borderRadius: 2 }}
          >
            {ocupado ? "Abriendo…" : "Administrar mi cuenta en Stripe"}
          </Button>
        </Paper>
      ) : (
        /* No conectado, o conectado pero pendiente de verificación */
        <Paper
          variant="outlined"
          sx={{ p: { xs: 3, md: 4 }, borderRadius: 3, bgcolor: "grey.50" }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2.5 }}>
            <Box sx={{
              width: 48, height: 48, borderRadius: "12px", flexShrink: 0,
              // Tonos claros del amarillo y del azul medio del tema (antes #FFF4DD y #EEF2FF a mano)
              bgcolor: (t) => (conectado ? alpha(t.palette.warning.main, 0.25) : alpha(t.palette.secondary.main, 0.12)),
              display: "flex", alignItems: "center", justifyContent: "center",
            }}>
              {conectado
                ? <HourglassEmptyOutlinedIcon sx={{ color: "primary.main", fontSize: 26 }} />
                : <AccountBalanceOutlinedIcon sx={{ color: "secondary.main", fontSize: 26 }} />}
            </Box>
            <Box>
              <Typography variant="h6" fontWeight="bold" color="primary">
                {conectado
                  ? (enviado ? "Estamos verificando tu cuenta" : "Continúa tu registro")
                  : "Conecta tu cuenta para recibir tus pagos"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {conectado
                  ? (enviado
                    ? "Stripe está revisando tu información. Te avisaremos en cuanto puedas recibir pagos."
                    : "Te faltan algunos datos para terminar de habilitar tus depósitos.")
                  : "Verificamos tu identidad y registramos tu cuenta bancaria de forma segura con Stripe."}
              </Typography>
            </Box>
          </Box>

          {conectado && enviado && (
            <Alert severity="info" sx={{ borderRadius: 2, mb: 2.5 }}>
              La verificación suele tardar poco. Si Stripe necesita algo más, puedes actualizar
              tu información con el botón de abajo.
            </Alert>
          )}

          {!conectado && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25, mb: 3 }}>
              <Beneficio>Tus depósitos son <strong>automáticos</strong>: al completar un servicio te transferimos tu parte.</Beneficio>
              <Beneficio>Stripe resguarda tus datos bancarios; <strong>nosotros no los almacenamos</strong>.</Beneficio>
              <Beneficio>La plataforma <strong>absorbe la comisión</strong> del procesador; tú recibes tu 90%.</Beneficio>
            </Box>
          )}

          <Button
            variant="contained"
            onClick={handleConectar}
            disabled={ocupado}
            startIcon={!ocupado && <BoltOutlinedIcon />}
            sx={{ textTransform: "none", fontWeight: "bold", borderRadius: 2, height: 44, px: 3 }}
          >
            {ocupado
              ? "Redirigiendo a Stripe…"
              : conectado
                ? (enviado ? "Actualizar información" : "Continuar registro")
                : "Conectar mi cuenta"}
          </Button>
        </Paper>
      )}

      {/* Cómo pagas tus reservas */}
      <Box mt={4}>
        <SectionHeader
          title="Pagos que realizas"
          subtitle="Tus tarjetas se capturan de forma segura al momento de reservar"
        />
      </Box>
      <Paper
        variant="outlined"
        sx={{ p: { xs: 2.5, md: 4 }, bgcolor: "grey.50", borderRadius: 3, display: "flex", alignItems: "center", gap: 2 }}
      >
        <AccountBalanceOutlinedIcon sx={{ color: "secondary.main", fontSize: 32, flexShrink: 0 }} />
        <Typography variant="body2" color="text.secondary">
          Al reservar un servicio ingresas tu tarjeta y se procesa con cifrado a través de
          Stripe. El cargo se realiza únicamente cuando el proveedor acepta tu reserva, y
          nunca almacenamos los datos de tu tarjeta en nuestros servidores.
        </Typography>
      </Paper>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {toast && (
          <Alert severity={toast.tipo} onClose={() => setToast(null)}>
            {toast.msg}
          </Alert>
        )}
      </Snackbar>
    </Box>
  );
};

export default Payments;
